import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

const cx = (...parts: (string | false | undefined)[]): string => parts.filter(Boolean).join(" ");

export function Button({ variant = "primary", className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" }) {
  return <button {...rest} className={cx("btn", variant === "primary" ? "btn-primary" : "btn-secondary", className)} />;
}

interface FieldProps {
  id: string;
  label: string;
  optional?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ id, label, optional, hint, error, children }: FieldProps) {
  return (
    <div className={cx("field", error && "has-error")}>
      <label htmlFor={id}>
        {label} {optional && <span className="optional">(optional)</span>}
      </label>
      {children}
      {hint && <p className="hint">{hint}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

// An empty value reads as the greyed-out "Select an option" placeholder, as in the prototype.
export function Select({
  options,
  placeholder = "Select an option",
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: readonly string[]; placeholder?: string }) {
  return (
    <div className="select-wrap">
      <select {...rest}>
        {rest.required ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : (
          <option value="">{placeholder}</option>
        )}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="select-arrow">▾</span>
    </div>
  );
}

export const SeverityPill = ({ severity }: { severity: string }) => <span className={`severity-pill severity-${severity}`}>{severity}</span>;

export const ErrorBanner = ({ children }: { children: ReactNode }) => (
  <div className="error-banner" role="alert">
    {children}
  </div>
);
